import React, { useState, useEffect, useCallback } from 'react'
import {
  Card,
  Row,
  Col,
  Tag,
  Button,
  Spin,
  Typography,
  Space,
  Table,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Alert,
  message,
  Descriptions
} from 'antd'
import {
  CloudUploadOutlined,
  ReloadOutlined,
  RedoOutlined,
  SyncOutlined,
  DeleteOutlined,
  DatabaseOutlined,
  SearchOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import Layout from '../Home'
import * as twilioRecordingsServices from '../../apiServices/twilioRecordingsServices'
import moment from 'moment'

const { Title, Text } = Typography
const { RangePicker } = DatePicker

/** สร้างลิงก์เปิดโฟลเดอร์/ไฟล์ใน Firebase Storage Console */
function getFirebaseStorageConsoleUrl(bucket, gcsObject) {
  if (!bucket || !gcsObject) return null
  const projectId = bucket.replace(/\.appspot\.com$/, '') || bucket
  const pathEncoded = gcsObject.replace(/\//g, '~2F')
  return `https://console.firebase.google.com/project/${projectId}/storage/${bucket}/files/~2F${pathEncoded}`
}

const Container = styled.div`
  padding: 16px;
  background: #f5f5f5;
  min-height: 100vh;
  width: 100%;
`

const HeaderCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  .ant-card-body {
    padding: 20px;
  }
`

const STATE_OPTIONS = [
  'DISCOVERED',
  'QUEUED',
  'TRANSFERRING',
  'UPLOADED',
  'DELETED',
  'FAILED'
]

const stateColorMap = {
  DISCOVERED: 'default',
  QUEUED: 'blue',
  TRANSFERRING: 'processing',
  UPLOADED: 'success',
  DELETED: 'default',
  FAILED: 'error'
}

const TwilioRecordings = (props) => {
  const { history } = props
  const [summary, setSummary] = useState(null)
  const [list, setList] = useState([])
  const [total, setTotal] = useState(0)
  const [loadingSummary, setLoadingSummary] = useState(false)
  const [loadingList, setLoadingList] = useState(false)
  const [syncModalVisible, setSyncModalVisible] = useState(false)
  const [syncLoading, setSyncLoading] = useState(false)
  const [syncAllLoading, setSyncAllLoading] = useState(false)
  const [syncFromStorageLoading, setSyncFromStorageLoading] = useState(false)
  const [queueMoveAllLoading, setQueueMoveAllLoading] = useState(false)
  const [requeueQueuedAllLoading, setRequeueQueuedAllLoading] = useState(false)
  const [checkDuplicateLoading, setCheckDuplicateLoading] = useState(false)
  const [checkAndDeleteLoading, setCheckAndDeleteLoading] = useState(false)
  const [fireMoveRecordingTestLoading, setFireMoveRecordingTestLoading] = useState(false)
  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false)
  const [duplicateResult, setDuplicateResult] = useState(null)
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [detailRow, setDetailRow] = useState(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [filters, setFilters] = useState({
    state: undefined,
    roomSid: undefined,
    dateFrom: undefined,
    dateTo: undefined,
    q: undefined,
    page: 1,
    limit: 20
  })
  const [syncForm] = Form.useForm()

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true)
    try {
      const res = await twilioRecordingsServices.getSummary()
      const data = res?.data ?? res
      setSummary(data)
    } catch (err) {
      message.error(err?.response?.data?.error?.message || 'โหลด summary ไม่สำเร็จ')
      setSummary(null)
    } finally {
      setLoadingSummary(false)
    }
  }, [])

  const fetchList = useCallback(async () => {
    setLoadingList(true)
    try {
      const params = {
        ...filters,
        dateFrom: filters.dateFrom ? filters.dateFrom.valueOf() : undefined,
        dateTo: filters.dateTo ? filters.dateTo.valueOf() : undefined
      }
      const res = await twilioRecordingsServices.getList(params)
      const responseWithData = res.data
      const items = Array.isArray(responseWithData?.data) ? responseWithData.data : []
      const totalCount = responseWithData?.meta?.totalItemCount ?? 0
      setList(items)
      setTotal(totalCount)
    } catch (err) {
      message.error(err?.response?.data?.error?.message || 'โหลดรายการไม่สำเร็จ')
      setList([])
      setTotal(0)
    } finally {
      setLoadingList(false)
    }
  }, [filters])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const handleSync = () => {
    syncForm.resetFields()
    setSyncModalVisible(true)
  }

  const handleSyncAll = () => {
    Modal.confirm({
      title: 'Sync ทั้งหมดจาก Twilio (ครั้งแรก)',
      content: 'จะดึงรายการ recording ทั้งหมดจาก Twilio แบบแบ่งชุด (ชุดละ 2,000 รายการ) จนครบ เช่น 80K จะใช้ประมาณ 40 ครั้ง ต้องการดำเนินการต่อหรือไม่?',
      okText: 'Sync ทั้งหมด',
      cancelText: 'ยกเลิก',
      onOk: async () => {
        try {
          setSyncAllLoading(true)
          let nextPageToken = null
          let totalSynced = 0
          let totalCreated = 0
          let totalUpdated = 0
          const batchSize = 2000

          do {
            const res = await twilioRecordingsServices.syncAll(nextPageToken || undefined, batchSize)
            const data = res?.data ?? res
            totalSynced += data.synced || 0
            totalCreated += data.created || 0
            totalUpdated += data.updated || 0
            nextPageToken = data.nextPageToken || null
            if (nextPageToken) {
              message.loading({ content: `กำลัง sync... ${totalSynced} รายการแล้ว`, key: 'syncAll', duration: 0 })
            }
          } while (nextPageToken)

          message.success({
            content: `Sync ทั้งหมดสำเร็จ: รวม ${totalSynced} รายการ (สร้าง ${totalCreated} อัปเดต ${totalUpdated})`,
            key: 'syncAll'
          })
          fetchSummary()
          fetchList()
        } catch (err) {
          message.error({ content: err?.response?.data?.error?.message || 'Sync ทั้งหมดไม่สำเร็จ', key: 'syncAll' })
        } finally {
          setSyncAllLoading(false)
        }
      }
    })
  }

  const handleSyncFromStorage = () => {
    Modal.confirm({
      title: 'Sync จาก Storage',
      content: 'จะสแกนไฟล์ใน Firebase Storage (twilio-record/) แล้วอัปเดต DB เป็น UPLOADED สำหรับรายการที่ไฟล์มีอยู่แล้วแต่ DB ยังไม่อัปเดต ต้องการดำเนินการต่อหรือไม่?',
      okText: 'Sync จาก Storage',
      cancelText: 'ยกเลิก',
      onOk: async () => {
        try {
          setSyncFromStorageLoading(true)
          let nextPageToken = null
          let totalUpdated = 0
          let totalProcessed = 0
          const batchSize = 1000

          do {
            const res = await twilioRecordingsServices.syncFromStorage(batchSize, nextPageToken || undefined)
            const data = res?.data ?? res
            totalUpdated += data.updated || 0
            totalProcessed += data.processed || 0
            nextPageToken = data.nextPageToken || null
            if (nextPageToken) {
              message.loading({
                content: `กำลัง sync จาก storage... ประมวลผล ${totalProcessed} อัปเดต ${totalUpdated} รายการ`,
                key: 'syncFromStorage',
                duration: 0
              })
            }
          } while (nextPageToken)

          message.success({
            content: `Sync จาก Storage สำเร็จ: อัปเดต ${totalUpdated} รายการ (ประมวลผล ${totalProcessed} ไฟล์)`,
            key: 'syncFromStorage'
          })
          fetchSummary()
          fetchList()
        } catch (err) {
          message.error({
            content: err?.response?.data?.error?.message || 'Sync จาก Storage ไม่สำเร็จ',
            key: 'syncFromStorage'
          })
        } finally {
          setSyncFromStorageLoading(false)
        }
      }
    })
  }

  const handleCheckDuplicateUploads = async () => {
    try {
      setCheckDuplicateLoading(true)
      setDuplicateResult(null)
      const res = await twilioRecordingsServices.checkDuplicateUploads()
      const data = res?.data ?? res
      setDuplicateResult(data)
      setDuplicateModalVisible(true)
      if (data?.duplicateCount === 0) {
        message.success('ไม่พบ recording ที่อัปโหลดซ้ำ')
      }
    } catch (err) {
      message.error(err?.response?.data?.error?.message || 'เช็ค duplicate ไม่สำเร็จ')
    } finally {
      setCheckDuplicateLoading(false)
    }
  }

  const handleCheckAndDeleteDuplicates = () => {
    Modal.confirm({
      title: 'เช็คและลบ Duplicate',
      content: 'จะสแกน GCS หา recording ที่อัปโหลดซ้ำ แล้วลบไฟล์ซ้ำออก (เก็บไฟล์เดียวตาม DB หรือไฟล์แรก) ต้องการดำเนินการต่อหรือไม่?',
      okText: 'เช็คและลบ',
      cancelText: 'ยกเลิก',
      onOk: async () => {
        try {
          setCheckAndDeleteLoading(true)
          let totalDeleted = 0
          let round = 0
          let data
          do {
            round++
            const res = await twilioRecordingsServices.checkAndDeleteDuplicates()
            data = res?.data ?? res
            totalDeleted += data?.deleted || 0
            if ((data?.deleted || 0) > 0) {
              message.loading({ content: `รอบที่ ${round}: ลบ ${data.deleted} ไฟล์แล้ว (รวม ${totalDeleted})`, key: 'checkAndDelete', duration: 0 })
            }
          } while ((data?.deleted || 0) > 0 && round < 10)
          if (totalDeleted > 0) {
            message.success({ content: `ลบ duplicate เสร็จ: รวม ${totalDeleted} ไฟล์`, key: 'checkAndDelete' })
          } else {
            message.success({ content: 'ไม่พบ recording ที่อัปโหลดซ้ำ', key: 'checkAndDelete' })
          }
          setDuplicateResult(data)
          setDuplicateModalVisible(true)
          fetchSummary()
        } catch (err) {
          message.error({ content: err?.response?.data?.error?.message || 'เช็คและลบ duplicate ไม่สำเร็จ', key: 'checkAndDelete' })
        } finally {
          setCheckAndDeleteLoading(false)
        }
      }
    })
  }

  const handleResetQueuedToFailed = () => {
    Modal.confirm({
      title: 'ลบ Queue ใน Summary',
      content: 'จะรีเซ็ต state QUEUED ทั้งหมดเป็น FAILED ทำให้ตัวเลข QUEUED ใน Summary เป็น 0 (ใช้เมื่อลบ task ใน Cloud Tasks ไปแล้ว) ต้องการดำเนินการต่อหรือไม่?',
      okText: 'รีเซ็ต',
      cancelText: 'ยกเลิก',
      onOk: async () => {
        try {
          const res = await twilioRecordingsServices.resetQueuedToFailed()
          const data = res?.data ?? res
          message.success(`รีเซ็ตแล้ว: ${data?.updated ?? 0} รายการ จาก QUEUED เป็น FAILED`)
          fetchSummary()
          fetchList()
        } catch (err) {
          message.error(err?.response?.data?.error?.message || 'รีเซ็ตไม่สำเร็จ')
        }
      }
    })
  }

  const handleRequeueQueuedAll = () => {
    Modal.confirm({
      title: 'Re-queue QUEUED',
      content: 'จะสร้าง Cloud Task ใหม่ให้รายการที่ค้างใน state QUEUED (เช่น task หายไปจาก queue เพราะ timeout) แบบแบ่งชุด ต้องการดำเนินการต่อหรือไม่?',
      okText: 'Re-queue',
      cancelText: 'ยกเลิก',
      onOk: async () => {
        try {
          setRequeueQueuedAllLoading(true)
          let nextSkip = 0
          let totalQueued = 0
          let totalSkipped = 0
          const batchSize = 500

          do {
            const res = await twilioRecordingsServices.requeueQueuedAll(nextSkip, batchSize)
            const data = res?.data ?? res
            totalQueued += data.queued || 0
            totalSkipped += data.skipped || 0
            nextSkip = data.nextSkip
            if (nextSkip != null) {
              message.loading({ content: `กำลัง re-queue... ${totalQueued + totalSkipped} รายการแล้ว`, key: 'requeueQueuedAll', duration: 0 })
            }
          } while (nextSkip != null)

          message.success({
            content: totalQueued === 0 && totalSkipped > 0
              ? `Re-queue QUEUED: ข้าม ${totalSkipped} รายการ (มี task ใน queue อยู่แล้ว ไม่สร้างซ้ำ)`
              : `Re-queue QUEUED สำเร็จ: สร้าง task ใหม่ ${totalQueued} รายการ (ข้าม ${totalSkipped})`,
            key: 'requeueQueuedAll'
          })
          fetchSummary()
          fetchList()
        } catch (err) {
          message.error({ content: err?.response?.data?.error?.message || 'Re-queue QUEUED ไม่สำเร็จ', key: 'requeueQueuedAll' })
        } finally {
          setRequeueQueuedAllLoading(false)
        }
      }
    })
  }

  const handleQueueMoveAll = () => {
    Modal.confirm({
      title: 'โหลดเข้า GCS ทั้งหมด',
      content: 'จะ queue รายการที่ยังไม่ได้อัปโหลด (DISCOVERED/FAILED) ทั้งหมดให้โหลดเข้า GCS แบบแบ่งชุด ต้องการดำเนินการต่อหรือไม่?',
      okText: 'โหลดเข้า GCS',
      cancelText: 'ยกเลิก',
      onOk: async () => {
        try {
          setQueueMoveAllLoading(true)
          let nextSkip = 0
          let totalQueued = 0
          let totalSkipped = 0
          const batchSize = 500

          do {
            const res = await twilioRecordingsServices.queueMoveAll(nextSkip, batchSize)
            const data = res?.data ?? res
            totalQueued += data.queued || 0
            totalSkipped += data.skipped || 0
            nextSkip = data.nextSkip
            if (nextSkip != null) {
              message.loading({ content: `กำลัง queue... ${totalQueued + totalSkipped} รายการแล้ว`, key: 'queueMoveAll', duration: 0 })
            }
          } while (nextSkip != null)

          message.success({
            content: `โหลดเข้า GCS สำเร็จ: queue ${totalQueued} รายการ (ข้าม ${totalSkipped})`,
            key: 'queueMoveAll'
          })
          fetchSummary()
          fetchList()
        } catch (err) {
          message.error({ content: err?.response?.data?.error?.message || 'โหลดเข้า GCS ไม่สำเร็จ', key: 'queueMoveAll' })
        } finally {
          setQueueMoveAllLoading(false)
        }
      }
    })
  }

  const handleSyncSubmit = async () => {
    try {
      const values = await syncForm.validateFields()
      const body = {}
      if (values.dateRange?.[0]) body.dateFrom = values.dateRange[0].toISOString()
      if (values.dateRange?.[1]) body.dateTo = values.dateRange[1].toISOString()
      if (values.limit != null && values.limit > 0) body.limit = values.limit
      setSyncLoading(true)
      await twilioRecordingsServices.sync(body)
      message.success('Sync จาก Twilio สำเร็จ')
      setSyncModalVisible(false)
      fetchSummary()
      fetchList()
    } catch (err) {
      if (err?.errorFields) return
      message.error(err?.response?.data?.error?.message || 'Sync ไม่สำเร็จ')
    } finally {
      setSyncLoading(false)
    }
  }

  const handleQueueMove = async (id) => {
    try {
      await twilioRecordingsServices.queueMove(id)
      message.success('เพิ่มคิวย้ายสำเร็จ')
      fetchSummary()
      fetchList()
    } catch (err) {
      message.error(err?.response?.data?.error?.message || 'เพิ่มคิวไม่สำเร็จ')
    }
  }

  const handleBulkQueueMove = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('กรุณาเลือกรายการ')
      return
    }
    try {
      await twilioRecordingsServices.bulkQueueMove(selectedRowKeys)
      message.success('เพิ่มคิวย้ายที่เลือกสำเร็จ')
      setSelectedRowKeys([])
      fetchSummary()
      fetchList()
    } catch (err) {
      message.error(err?.response?.data?.error?.message || 'เพิ่มคิวไม่สำเร็จ')
    }
  }

  const handleFireMoveRecordingTest = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('กรุณาเลือกรายการ')
      return
    }
    setFireMoveRecordingTestLoading(true)
    let ok = 0
    let fail = 0
    try {
      for (const sid of selectedRowKeys) {
        try {
          await twilioRecordingsServices.fireMoveRecordingTest(sid)
          ok += 1
        } catch (e) {
          fail += 1
          console.warn('fireMoveRecordingTest failed:', sid, e?.response?.data || e.message)
        }
      }
      if (fail === 0) {
        message.success(`ยิง moveRecording สำเร็จ ${ok} รายการ`)
      } else {
        message.warning(`ยิง moveRecording: สำเร็จ ${ok} ล้มเหลว ${fail}`)
      }
      setSelectedRowKeys([])
      fetchSummary()
      fetchList()
    } catch (err) {
      message.error(err?.message || 'ยิง moveRecording ไม่สำเร็จ')
    } finally {
      setFireMoveRecordingTestLoading(false)
    }
  }

  const handleRetry = async (id) => {
    try {
      await twilioRecordingsServices.retry(id)
      message.success('Retry สำเร็จ')
      fetchSummary()
      fetchList()
    } catch (err) {
      message.error(err?.response?.data?.error?.message || 'Retry ไม่สำเร็จ')
    }
  }

  const handleDeleteTwilio = async (id) => {
    try {
      await twilioRecordingsServices.deleteTwilio(id)
      message.success('ลบบน Twilio สำเร็จ')
      fetchSummary()
      fetchList()
    } catch (err) {
      message.error(err?.response?.data?.error?.message || 'ลบไม่สำเร็จ')
    }
  }

  const formatTimestamp = (v) => {
    if (!v) return '-'
    const d = new Date(v)
    return d.toLocaleString('th-TH')
  }

  const formatBytes = (bytes) => {
    if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '-'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const columns = [
    {
      title: 'Recording SID',
      dataIndex: 'recordingSid',
      key: 'recordingSid',
      ellipsis: true,
      render: (val) => val ?? '-'
    },
    {
      title: 'Room SID',
      dataIndex: 'roomSid',
      key: 'roomSid',
      ellipsis: true,
      width: 180
    },
    {
      title: 'Participant SID',
      dataIndex: 'participantSid',
      key: 'participantSid',
      ellipsis: true,
      width: 180
    },
    {
      title: 'สร้างเมื่อ',
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      width: 160,
      render: (v) => formatTimestamp(v)
    },
    {
      title: 'ระยะเวลา (วินาที)',
      dataIndex: 'durationSec',
      key: 'durationSec',
      width: 100
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 80
    },
    {
      title: 'Track',
      dataIndex: 'trackName',
      key: 'trackName',
      width: 100
    },
    {
      title: 'Codec',
      dataIndex: 'codec',
      key: 'codec',
      width: 80
    },
    {
      title: 'Container',
      dataIndex: 'containerFormat',
      key: 'containerFormat',
      width: 90
    },
    {
      title: 'ขนาด (Twilio)',
      dataIndex: 'twilioSize',
      key: 'twilioSize',
      width: 100,
      render: (v) => formatBytes(v)
    },
    {
      title: 'Twilio Status',
      dataIndex: 'twilioStatus',
      key: 'twilioStatus',
      width: 100
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: 120,
      render: (state) => (
        <Tag color={stateColorMap[state] || 'default'}>{state || '-'}</Tag>
      )
    },
    {
      title: 'GCS Object',
      dataIndex: 'gcsObject',
      key: 'gcsObject',
      ellipsis: true,
      render: (gcsObject, row) => {
        if (!gcsObject) return '-'
        const bucket = row.gcsBucket
        const url = getFirebaseStorageConsoleUrl(bucket, gcsObject)
        return url
          ? <a href={url} target="_blank" rel="noopener noreferrer" title="เปิดใน Firebase Storage">{gcsObject}</a>
          : gcsObject
      }
    },
    {
      title: 'Attempts',
      dataIndex: 'attempts',
      key: 'attempts',
      width: 80
    },
    {
      title: 'Last Error',
      dataIndex: 'lastError',
      key: 'lastError',
      ellipsis: true
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      fixed: 'right',
      render: (_, row) => {
        const id = row.recordingSid
        const state = row.state
        const hasGcs = !!row.gcsObject
        const gcsUrl = getFirebaseStorageConsoleUrl(row.gcsBucket, row.gcsObject)
        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              onClick={() => { setDetailRow(row); setDetailModalVisible(true) }}
            >
              รายละเอียด
            </Button>
            {gcsUrl && (
              <Button
                type="link"
                size="small"
                onClick={() => window.open(gcsUrl, '_blank', 'noopener,noreferrer')}
                title="เปิดใน Firebase Storage Console"
              >
                ดูใน GCS
              </Button>
            )}
            {(state === 'DISCOVERED' || state === 'QUEUED' || state === 'FAILED' || state === 'TRANSFERRING') && (
              <Button
                type="link"
                size="small"
                icon={<CloudUploadOutlined />}
                onClick={() => handleQueueMove(id)}
              >
                คิวย้าย
              </Button>
            )}
            {(state === 'FAILED' || state === 'TRANSFERRING') && (
              <Button
                type="link"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => handleRetry(id)}
              >
                Retry
              </Button>
            )}
            {state === 'UPLOADED' && hasGcs && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteTwilio(id)}
              >
                ลบบน Twilio
              </Button>
            )}
          </Space>
        )
      }
    }
  ]

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    getCheckboxProps: (row) => ({
      disabled: !['DISCOVERED', 'QUEUED', 'FAILED', 'TRANSFERRING'].includes(row.state)
    })
  }

  return (
    <Layout
      history={history}
      render={() => (
        <Container>
          <HeaderCard>
            <Row>
              <Col span={24}>
                <Title level={2} style={{ margin: 0 }}>
                  Twilio Recordings
                </Title>
              </Col>
            </Row>
            <Row style={{ marginTop: 12 }}>
              <Col span={24}>
                <Space wrap size="small">
              <Button
                type="primary"
                icon={<SyncOutlined />}
                onClick={handleSync}
              >
                Sync จาก Twilio
              </Button>
              <Button
                type="default"
                icon={<SyncOutlined />}
                loading={syncAllLoading}
                onClick={handleSyncAll}
              >
                Sync ทั้งหมด (ครั้งแรก)
              </Button>
              <Button
                type="default"
                icon={<CloudUploadOutlined />}
                loading={queueMoveAllLoading}
                onClick={handleQueueMoveAll}
              >
                โหลดเข้า GCS ทั้งหมด
              </Button>
              <Button
                type="default"
                icon={<DatabaseOutlined />}
                loading={syncFromStorageLoading}
                onClick={handleSyncFromStorage}
                title="สแกนไฟล์ใน Firebase Storage แล้วอัปเดต DB เป็น UPLOADED สำหรับรายการที่ไฟล์มีอยู่แล้ว"
              >
                Sync จาก Storage
              </Button>
              <Button
                type="default"
                icon={<SearchOutlined />}
                loading={checkDuplicateLoading}
                onClick={handleCheckDuplicateUploads}
                title="สแกน GCS หา recording ที่อัปโหลดซ้ำ"
              >
                เช็ค Duplicate
              </Button>
              <Button
                type="default"
                danger
                icon={<DeleteOutlined />}
                loading={checkAndDeleteLoading}
                onClick={handleCheckAndDeleteDuplicates}
                title="สแกน GCS หา duplicate แล้วลบไฟล์ซ้ำออก"
              >
                เช็คและลบ Duplicate
              </Button>
              <Button
                type="default"
                icon={<RedoOutlined />}
                loading={requeueQueuedAllLoading}
                onClick={handleRequeueQueuedAll}
                title="สร้าง Cloud Task ใหม่ให้รายการที่ค้างใน QUEUED (task หายจาก queue)"
              >
                Re-queue QUEUED
              </Button>
                  <Button
                    danger
                    onClick={handleResetQueuedToFailed}
                    title="รีเซ็ต QUEUED เป็น FAILED ให้ตัวเลข QUEUED ใน Summary เป็น 0 (ใช้เมื่อลบ task ใน Cloud Tasks ไปแล้ว)"
                  >
                    ลบ Queue ใน Summary
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => { fetchSummary(); fetchList() }}
                  >
                    รีเฟรช
                  </Button>
                </Space>
              </Col>
            </Row>
          </HeaderCard>

          {loadingSummary ? (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <Spin />
            </div>
          ) : summary ? (
            <HeaderCard style={{ marginBottom: 16 }}>
              <Title level={5}>สรุป</Title>
              <Row gutter={16}>
                <Col>
                  <Text strong>ทั้งหมด: </Text>
                  <Text>{summary.totalCount ?? summary.total ?? '-'}</Text>
                </Col>
                {summary.countsByState && typeof summary.countsByState === 'object' && (
                  <Col>
                    <Space size="small">
                      {Object.entries(summary.countsByState).map(([s, c]) => (
                        <Tag key={s} color={stateColorMap[s] || 'default'}>
                          {s}: {c}
                        </Tag>
                      ))}
                    </Space>
                  </Col>
                )}
                <Col>
                  <Text strong>อัปโหลดแล้ว (bytes): </Text>
                  <Text>{formatBytes(summary.totalUploadedBytes)}</Text>
                </Col>
              </Row>
            </HeaderCard>
          ) : null}

          <HeaderCard>
            <Space wrap style={{ marginBottom: 16 }}>
              <Select
                placeholder="State"
                allowClear
                style={{ width: 140 }}
                value={filters.state}
                onChange={(v) => setFilters((f) => ({ ...f, state: v, page: 1 }))}
              >
                {STATE_OPTIONS.map((s) => (
                  <Select.Option key={s} value={s}>{s}</Select.Option>
                ))}
              </Select>
              <Input
                placeholder="Room SID"
                allowClear
                style={{ width: 200 }}
                value={filters.roomSid}
                onChange={(e) => setFilters((f) => ({ ...f, roomSid: e.target.value || undefined, page: 1 }))}
              />
              <RangePicker
                placeholder={['จากวันที่', 'ถึงวันที่']}
                value={filters.dateFrom && filters.dateTo ? [moment(filters.dateFrom), moment(filters.dateTo)] : null}
                onChange={(dates) =>
                  setFilters((f) => ({
                    ...f,
                    dateFrom: dates?.[0] ?? undefined,
                    dateTo: dates?.[1] ?? undefined,
                    page: 1
                  }))
                }
              />
              <Input
                placeholder="ค้นหา SID"
                allowClear
                style={{ width: 180 }}
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value || undefined, page: 1 }))}
              />
              <Button type="primary" onClick={fetchList}>
                ค้นหา
              </Button>
            </Space>

            {selectedRowKeys.length > 0 && (
              <Space style={{ marginBottom: 16 }}>
                <Button type="primary" icon={<CloudUploadOutlined />} onClick={handleBulkQueueMove}>
                  คิวย้ายที่เลือก ({selectedRowKeys.length})
                </Button>
                <Button
                  type="default"
                  loading={fireMoveRecordingTestLoading}
                  onClick={handleFireMoveRecordingTest}
                  title="ยิง POST /internal/jobs/moveRecording โดยตรง (ทดสอบ local) ไม่ผ่าน Cloud Tasks"
                >
                  ยิง moveRecording ทดสอบ ({selectedRowKeys.length})
                </Button>
              </Space>
            )}

            <Table
              rowKey="recordingSid"
              rowSelection={rowSelection}
              columns={columns}
              dataSource={list}
              loading={loadingList}
              pagination={{
                current: filters.page,
                pageSize: filters.limit,
                total,
                showSizeChanger: true,
                showTotal: (t) => `ทั้งหมด ${t} รายการ`,
                onChange: (page, pageSize) =>
                  setFilters((f) => ({ ...f, page, limit: pageSize || f.limit }))
              }}
              scroll={{ x: 1200 }}
            />
          </HeaderCard>

          <Modal
            title="Sync จาก Twilio"
            open={syncModalVisible}
            onOk={handleSyncSubmit}
            onCancel={() => !syncLoading && setSyncModalVisible(false)}
            okText="Sync"
            confirmLoading={syncLoading}
            closable={!syncLoading}
            maskClosable={!syncLoading}
          >
            {syncLoading && (
              <Alert
                message="กำลัง sync จาก Twilio..."
                description="กรุณารอสักครู่ กำลังดึงรายการ recording จาก Twilio"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            <Form form={syncForm} layout="vertical">
              <Form.Item
                name="dateRange"
                label="ช่วงวันที่"
                rules={[{ required: true, message: 'กรุณาเลือกช่วงวันที่' }]}
              >
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="limit"
                label="Limit (ไม่กรอก = ดึงทั้งหมดตามช่วงวันที่)"
              >
                <InputNumber min={1} max={10000} placeholder="ไม่จำกัด" style={{ width: '100%' }} />
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            title="รายละเอียด Recording (จาก Twilio)"
            open={detailModalVisible}
            onCancel={() => { setDetailModalVisible(false); setDetailRow(null) }}
            footer={<Button onClick={() => { setDetailModalVisible(false); setDetailRow(null) }}>ปิด</Button>}
            width={640}
          >
            {detailRow && (
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Recording SID">{detailRow.recordingSid}</Descriptions.Item>
                <Descriptions.Item label="Account SID">{detailRow.accountSid}</Descriptions.Item>
                <Descriptions.Item label="Room SID">{detailRow.roomSid}</Descriptions.Item>
                <Descriptions.Item label="Participant SID">{detailRow.participantSid}</Descriptions.Item>
                <Descriptions.Item label="Source SID">{detailRow.sourceSid}</Descriptions.Item>
                <Descriptions.Item label="สร้างเมื่อ">{formatTimestamp(detailRow.dateCreated)}</Descriptions.Item>
                <Descriptions.Item label="ระยะเวลา (วินาที)">{detailRow.durationSec}</Descriptions.Item>
                <Descriptions.Item label="Type">{detailRow.type}</Descriptions.Item>
                <Descriptions.Item label="Track Name">{detailRow.trackName}</Descriptions.Item>
                <Descriptions.Item label="Codec">{detailRow.codec}</Descriptions.Item>
                <Descriptions.Item label="Container Format">{detailRow.containerFormat}</Descriptions.Item>
                <Descriptions.Item label="ขนาด Twilio">{formatBytes(detailRow.twilioSize)}</Descriptions.Item>
                <Descriptions.Item label="Twilio Status">{detailRow.twilioStatus}</Descriptions.Item>
                <Descriptions.Item label="State"><Tag color={stateColorMap[detailRow.state]}>{detailRow.state}</Tag></Descriptions.Item>
                <Descriptions.Item label="Twilio URL">{detailRow.twilioUrl ? <a href={detailRow.twilioUrl} target="_blank" rel="noopener noreferrer">เปิดลิงก์</a> : '-'}</Descriptions.Item>
                <Descriptions.Item label="Media URL">{detailRow.mediaUrl ? <a href={detailRow.mediaUrl} target="_blank" rel="noopener noreferrer">เปิดลิงก์</a> : '-'}</Descriptions.Item>
                <Descriptions.Item label="GCS Object">
                  {detailRow.gcsObject
                    ? (() => {
                        const url = getFirebaseStorageConsoleUrl(detailRow.gcsBucket, detailRow.gcsObject)
                        return url ? <a href={url} target="_blank" rel="noopener noreferrer">เปิดใน Firebase Storage</a> : detailRow.gcsObject
                      })()
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="GCS Size">{formatBytes(detailRow.gcsSizeBytes)}</Descriptions.Item>
                <Descriptions.Item label="Attempts">{detailRow.attempts}</Descriptions.Item>
                <Descriptions.Item label="Last Error">{detailRow.lastError || '-'}</Descriptions.Item>
              </Descriptions>
            )}
          </Modal>

          <Modal
            title="Recording ที่อัปโหลดซ้ำใน GCS"
            open={duplicateModalVisible}
            onCancel={() => { setDuplicateModalVisible(false); setDuplicateResult(null) }}
            footer={<Button onClick={() => { setDuplicateModalVisible(false); setDuplicateResult(null) }}>ปิด</Button>}
            width={720}
          >
            {duplicateResult && (
              <>
                <p style={{ marginBottom: 16 }}>
                  <Text strong>รวมไฟล์:</Text> {duplicateResult.totalFiles ?? '-'} |{' '}
                  <Text strong>Unique recording:</Text> {duplicateResult.totalUnique ?? '-'} |{' '}
                  <Text strong>ซ้ำ:</Text> <Text type="danger">{duplicateResult.duplicateCount ?? 0}</Text> รายการ
                  {duplicateResult.deleted != null && (
                    <> | <Text strong>ลบไปแล้ว:</Text> <Text type="success">{duplicateResult.deleted}</Text> ไฟล์</>
                  )}
                </p>
                {duplicateResult.duplicates && duplicateResult.duplicates.length > 0 ? (
                  <div style={{ maxHeight: 400, overflow: 'auto' }}>
                    {duplicateResult.duplicates.map((d, i) => (
                      <div key={d.recordingSid || i} style={{ marginBottom: 16, padding: 12, background: '#fff7e6', borderRadius: 8 }}>
                        <Text strong>{d.recordingSid}</Text> — {d.count} ไฟล์
                        {d.deleted != null && <Text type="success"> (ลบ {d.deleted} ไฟล์)</Text>}
                        {d.paths && d.paths.length > 0 && (
                          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                            {d.paths.map((p, j) => (
                              <li key={j} style={{ wordBreak: 'break-all' }}><Text type="secondary">{p}</Text></li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>ไม่พบ recording ที่อัปโหลดซ้ำ</p>
                )}
                {duplicateResult.errors && duplicateResult.errors.length > 0 && (
                  <Alert
                    type="warning"
                    message="มี error บางรายการ"
                    description={
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {duplicateResult.errors.slice(0, 5).map((e, i) => (
                          <li key={i}>{e.recordingSid}: {e.error}</li>
                        ))}
                        {duplicateResult.errors.length > 5 && <li>... และอีก {duplicateResult.errors.length - 5} รายการ</li>}
                      </ul>
                    }
                    style={{ marginTop: 16 }}
                  />
                )}
              </>
            )}
          </Modal>
        </Container>
      )}
    />
  )
}

export default TwilioRecordings
